import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { useSearchParams } from 'react-router-dom';
import { blockBackdropClose, FormDialogTitle, LoadingState } from '../components/common';
import { toSafeImageUrl } from '../components/common/safeUrl';
import {
  goodsRepresentativeService,
  type GoodsItem,
  type GoodsRepresentativeEvent,
} from '../services/goodsRepresentativeService';

type RepresentativeEditMode = 'create' | 'update';

const CROPPED_IMAGE_SIZE = 1024;
const DEFAULT_CROP: Point = { x: 0, y: 0 };
const DEFAULT_CROP_BOX_SIZE = 280;
const MIN_CROP_BOX_SIZE = 160;
const MAX_CROP_BOX_SIZE = 460;
const GOODS_EVENT_ID_PARAM = 'goodsEventId';

function getImageStatus(event: GoodsRepresentativeEvent) {
  return event.imageUrl ? '대표 이미지 등록됨' : '대표 이미지 미등록';
}

async function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function cropImage(sourceUrl: string, filename: string, croppedAreaPixels: Area) {
  const image = await loadImage(sourceUrl);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) throw new Error('이미지를 편집할 수 없습니다.');

  canvas.width = CROPPED_IMAGE_SIZE;
  canvas.height = CROPPED_IMAGE_SIZE;
  context.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    CROPPED_IMAGE_SIZE,
    CROPPED_IMAGE_SIZE
  );

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('이미지를 편집할 수 없습니다.'));
        return;
      }
      resolve(new File([blob], filename, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  });
}

function GoodsRepresentativeImagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<GoodsRepresentativeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRepresentative, setSavingRepresentative] = useState(false);
  const [deletingRepresentative, setDeletingRepresentative] = useState(false);
  const [activeEvent, setActiveEvent] = useState<GoodsRepresentativeEvent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GoodsRepresentativeEvent | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState('');
  const [crop, setCrop] = useState<Point>(DEFAULT_CROP);
  const [cropBoxSize, setCropBoxSize] = useState(DEFAULT_CROP_BOX_SIZE);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [selectedGoodsEvent, setSelectedGoodsEvent] = useState<GoodsRepresentativeEvent | null>(null);
  const goodsRequest = useRef({ eventId: null as number | null, version: 0 });
  const [goods, setGoods] = useState<GoodsItem[]>([]);
  const [goodsLoading, setGoodsLoading] = useState(false);
  const [goodsSaving, setGoodsSaving] = useState(false);
  const [goodsDeleting, setGoodsDeleting] = useState(false);
  const [goodsDialogOpen, setGoodsDialogOpen] = useState(false);
  const [goodsDeleteTarget, setGoodsDeleteTarget] = useState<GoodsItem | null>(null);
  const [goodsName, setGoodsName] = useState('');
  const [goodsDescription, setGoodsDescription] = useState('');
  const [goodsImage, setGoodsImage] = useState<File | null>(null);
  const [goodsPreviewUrl, setGoodsPreviewUrl] = useState('');
  const [goodsCrop, setGoodsCrop] = useState<Point>(DEFAULT_CROP);
  const [goodsCropBoxSize, setGoodsCropBoxSize] = useState(DEFAULT_CROP_BOX_SIZE);
  const [goodsCroppedAreaPixels, setGoodsCroppedAreaPixels] = useState<Area | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogErrorMessage, setDialogErrorMessage] = useState('');
  const [goodsDialogErrorMessage, setGoodsDialogErrorMessage] = useState('');

  const registeredCount = useMemo(
    () => events.filter((event) => Boolean(event.imageUrl)).length,
    [events]
  );

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await goodsRepresentativeService.listEvents();
      setEvents(response.events);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : '굿즈 이미지 대회 목록을 불러오지 못했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGoods = useCallback(async (eventId: number) => {
    if (goodsRequest.current.eventId !== eventId) return;
    const version = ++goodsRequest.current.version;
    const isCurrentRequest = () =>
      goodsRequest.current.eventId === eventId && goodsRequest.current.version === version;
    setGoods([]);
    setGoodsLoading(true);
    setErrorMessage('');
    try {
      const response = await goodsRepresentativeService.listGoods(eventId);
      if (!isCurrentRequest()) return;
      setGoods(response.goods);
      setErrorMessage('');
    } catch (error) {
      if (!isCurrentRequest()) return;
      setErrorMessage(
        error instanceof Error ? error.message : '대회별 굿즈 목록을 불러오지 못했습니다.'
      );
    } finally {
      if (isCurrentRequest()) setGoodsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const requestState = goodsRequest.current;
    const eventId = Number(searchParams.get(GOODS_EVENT_ID_PARAM));
    if (!Number.isInteger(eventId) || eventId <= 0 || events.length === 0) {
      goodsRequest.current.eventId = null;
      goodsRequest.current.version += 1;
      setSelectedGoodsEvent(null);
      setGoods([]);
      setGoodsLoading(false);
      return;
    }

    const event = events.find((item) => item.eventId === eventId);
    if (!event) {
      setSearchParams({}, { replace: true });
      return;
    }

    goodsRequest.current.eventId = event.eventId;
    setSelectedGoodsEvent(event);
    void loadGoods(event.eventId);
    return () => {
      requestState.eventId = null;
      requestState.version += 1;
    };
  }, [events, loadGoods, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedImage) return undefined;

    const objectUrl = URL.createObjectURL(selectedImage);
    setSourcePreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedImage]);

  useEffect(() => {
    if (!goodsImage) return undefined;

    const objectUrl = URL.createObjectURL(goodsImage);
    setGoodsPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [goodsImage]);

  const openEditDialog = (event: GoodsRepresentativeEvent) => {
    setActiveEvent(event);
    setSelectedImage(null);
    setSourcePreviewUrl(toSafeImageUrl(event.imageUrl));
    setCrop(DEFAULT_CROP);
    setCropBoxSize(DEFAULT_CROP_BOX_SIZE);
    setCroppedAreaPixels(null);
    setDialogErrorMessage('');
  };

  const closeEditDialog = () => {
    if (!savingRepresentative) {
      setActiveEvent(null);
      setSelectedImage(null);
      setSourcePreviewUrl('');
      setCrop(DEFAULT_CROP);
      setCropBoxSize(DEFAULT_CROP_BOX_SIZE);
      setCroppedAreaPixels(null);
      setDialogErrorMessage('');
    }
  };

  const handleRepresentativeImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedImage(file);
    setCrop(DEFAULT_CROP);
    setCropBoxSize(DEFAULT_CROP_BOX_SIZE);
    setCroppedAreaPixels(null);
    setDialogErrorMessage('');
  };

  const handleCropBoxResizeStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const startSize = cropBoxSize;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const delta = Math.max(moveEvent.clientX - startX, moveEvent.clientY - startY);
      setCropBoxSize(
        Math.min(MAX_CROP_BOX_SIZE, Math.max(MIN_CROP_BOX_SIZE, startSize + delta * 2))
      );
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleRepresentativeSave = async () => {
    if (!activeEvent) return;

    if (!sourcePreviewUrl || !croppedAreaPixels) {
      setDialogErrorMessage('대표 이미지 파일을 선택해 주세요.');
      return;
    }

    const mode: RepresentativeEditMode = activeEvent.imageUrl ? 'update' : 'create';
    setSavingRepresentative(true);
    try {
      const filename = selectedImage?.name ?? `goods-representative-${activeEvent.eventId}.jpg`;
      const croppedImage = await cropImage(sourcePreviewUrl, filename, croppedAreaPixels);
      if (mode === 'create') {
        await goodsRepresentativeService.create(activeEvent.eventId, croppedImage);
      } else {
        await goodsRepresentativeService.update(activeEvent.eventId, croppedImage);
      }

      setActiveEvent(null);
      setSelectedImage(null);
      setSourcePreviewUrl('');
      setDialogErrorMessage('');
      setSuccessMessage('굿즈 대표 이미지를 저장했습니다.');
      await loadEvents();
    } catch (error) {
      setDialogErrorMessage(
        error instanceof Error ? error.message : '굿즈 대표 이미지를 저장하지 못했습니다.'
      );
    } finally {
      setSavingRepresentative(false);
    }
  };

  const handleRepresentativeDelete = async () => {
    if (!deleteTarget) return;

    setDeletingRepresentative(true);
    try {
      await goodsRepresentativeService.delete(deleteTarget.eventId);
      setDeleteTarget(null);
      setSuccessMessage('굿즈 대표 이미지를 삭제했습니다.');
      await loadEvents();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : '굿즈 대표 이미지를 삭제하지 못했습니다.'
      );
    } finally {
      setDeletingRepresentative(false);
    }
  };

  const openGoodsManagement = (event: GoodsRepresentativeEvent) => {
    goodsRequest.current.eventId = event.eventId;
    goodsRequest.current.version += 1;
    setGoods([]);
    setGoodsLoading(true);
    setSelectedGoodsEvent(event);
    setSearchParams({ [GOODS_EVENT_ID_PARAM]: String(event.eventId) });
    setSuccessMessage('');
  };

  const closeGoodsManagement = () => {
    goodsRequest.current.eventId = null;
    goodsRequest.current.version += 1;
    setSelectedGoodsEvent(null);
    setGoods([]);
    setGoodsLoading(false);
    setSearchParams({});
  };

  const closeGoodsDialog = () => {
    if (!goodsSaving) {
      setGoodsDialogOpen(false);
      setGoodsName('');
      setGoodsDescription('');
      setGoodsImage(null);
      setGoodsPreviewUrl('');
      setGoodsCrop(DEFAULT_CROP);
      setGoodsCropBoxSize(DEFAULT_CROP_BOX_SIZE);
      setGoodsCroppedAreaPixels(null);
      setGoodsDialogErrorMessage('');
    }
  };

  const handleGoodsImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setGoodsImage(file);
    setGoodsCrop(DEFAULT_CROP);
    setGoodsCropBoxSize(DEFAULT_CROP_BOX_SIZE);
    setGoodsCroppedAreaPixels(null);
    setGoodsDialogErrorMessage('');
  };

  const handleGoodsCropBoxResizeStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const startSize = goodsCropBoxSize;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const delta = Math.max(moveEvent.clientX - startX, moveEvent.clientY - startY);
      setGoodsCropBoxSize(
        Math.min(MAX_CROP_BOX_SIZE, Math.max(MIN_CROP_BOX_SIZE, startSize + delta * 2))
      );
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleGoodsCreate = async () => {
    if (!selectedGoodsEvent) return;

    if (!goodsName.trim()) {
      setGoodsDialogErrorMessage('굿즈 이름을 입력해 주세요.');
      return;
    }

    if (!goodsImage || !goodsPreviewUrl || !goodsCroppedAreaPixels) {
      setGoodsDialogErrorMessage('굿즈 이미지를 선택해 주세요.');
      return;
    }

    setGoodsSaving(true);
    try {
      const croppedGoodsImage = await cropImage(
        goodsPreviewUrl,
        goodsImage.name,
        goodsCroppedAreaPixels
      );
      await goodsRepresentativeService.createGoods(
        selectedGoodsEvent.eventId,
        { name: goodsName.trim(), description: goodsDescription.trim() },
        croppedGoodsImage
      );
      closeGoodsDialog();
      setSuccessMessage('굿즈를 추가했습니다.');
      await loadGoods(selectedGoodsEvent.eventId);
    } catch (error) {
      setGoodsDialogErrorMessage(
        error instanceof Error ? error.message : '굿즈를 추가하지 못했습니다.'
      );
    } finally {
      setGoodsSaving(false);
    }
  };

  const handleGoodsDelete = async () => {
    if (!selectedGoodsEvent || !goodsDeleteTarget) return;

    setGoodsDeleting(true);
    try {
      await goodsRepresentativeService.deleteGoods(
        selectedGoodsEvent.eventId,
        goodsDeleteTarget.id
      );
      setGoodsDeleteTarget(null);
      setSuccessMessage('굿즈를 삭제했습니다.');
      await loadGoods(selectedGoodsEvent.eventId);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '굿즈를 삭제하지 못했습니다.');
    } finally {
      setGoodsDeleting(false);
    }
  };

  if (selectedGoodsEvent) {
    return renderGoodsManagement();
  }

  return renderEventGrid();

  function renderGoodsManagement() {
    if (!selectedGoodsEvent) return null;

    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <div className="page-title-block">
            <p className="page-section">Homepage</p>
            <h1>굿즈 이미지 관리</h1>
            <p className="page-description">
              {selectedGoodsEvent.title} 대회에 노출할 굿즈 이미지와 설명을 관리합니다.
            </p>
          </div>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button onClick={closeGoodsManagement} variant="outlined">
              목록
            </Button>
            <Button onClick={() => setGoodsDialogOpen(true)} variant="contained">
              굿즈 추가
            </Button>
          </Box>

          {renderMessages()}

          {goodsLoading ? (
            <LoadingState message="굿즈 목록을 불러오는 중입니다." />
          ) : goods.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                borderColor: '#e2e8f0',
                boxShadow: 'none',
                color: '#94a3b8',
                p: 6,
                textAlign: 'center',
              }}
            >
              등록된 굿즈가 없습니다.
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              }}
            >
              {goods.map((item) => renderGoodsCard(item))}
            </Box>
          )}
        </div>

        {renderGoodsDialog()}
        {renderGoodsDeleteDialog()}
      </section>
    );
  }

  function renderGoodsCard(item: GoodsItem) {
    const safeImageUrl = toSafeImageUrl(item.imageUrl);

    return (
      <Paper
        key={item.id}
        variant="outlined"
        sx={{ borderColor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}
      >
        <Box
          alt={item.name}
          component="img"
          src={safeImageUrl}
          sx={{
            aspectRatio: '1 / 1',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'block',
            objectFit: 'contain',
            width: '100%',
          }}
        />
        <Box sx={{ display: 'grid', gap: 1, p: 2 }}>
          <Box sx={{ color: '#111827', fontSize: 15, fontWeight: 800 }}>{item.name}</Box>
          <Box sx={{ color: '#64748b', fontSize: 13, minHeight: 38 }}>
            {item.description || '설명 없음'}
          </Box>
          <Button
            color="error"
            onClick={() => setGoodsDeleteTarget(item)}
            size="small"
            variant="outlined"
          >
            삭제
          </Button>
        </Box>
      </Paper>
    );
  }

  function renderEventGrid() {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <div className="page-title-block">
            <p className="page-section">Homepage</p>
            <h1>굿즈 이미지 관리</h1>
            <p className="page-description">
              홈페이지 굿즈 영역에 표시할 대회별 대표 이미지와 굿즈 이미지를 관리합니다.
            </p>
          </div>

          {renderMessages()}

          <Paper
            variant="outlined"
            sx={{ borderColor: '#e2e8f0', boxShadow: 'none', mb: 2, p: 2 }}
          >
            <Box sx={{ color: '#475569', fontSize: 13, fontWeight: 700 }}>
              전체 {events.length}개 대회 · 대표 이미지 등록 {registeredCount}개
            </Box>
          </Paper>

          {loading ? (
            <LoadingState message="굿즈 이미지 대회 목록을 불러오는 중입니다." />
          ) : events.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                borderColor: '#e2e8f0',
                boxShadow: 'none',
                color: '#94a3b8',
                fontSize: 13,
                p: 6,
                textAlign: 'center',
              }}
            >
              조회할 대회가 없습니다.
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              }}
            >
              {events.map((event) => renderEventCard(event))}
            </Box>
          )}
        </div>

        {renderRepresentativeDialog()}
        {renderRepresentativeDeleteDialog()}
      </section>
    );
  }

  function renderEventCard(event: GoodsRepresentativeEvent) {
    const safeImageUrl = toSafeImageUrl(event.imageUrl);
    const hasImage = Boolean(safeImageUrl);

    return (
      <Paper
        key={event.eventId}
        variant="outlined"
        sx={{ borderColor: '#e2e8f0', borderRadius: 1, boxShadow: 'none', overflow: 'hidden' }}
      >
        <Box
          sx={{
            alignItems: 'center',
            aspectRatio: '1 / 1',
            backgroundColor: hasImage ? '#f8fafc' : '#f1f5f9',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {hasImage ? (
            <Box
              alt={`${event.title} 굿즈 대표 이미지`}
              component="img"
              src={safeImageUrl}
              sx={{ height: '100%', objectFit: 'contain', width: '100%' }}
            />
          ) : (
            <Box sx={{ color: '#94a3b8', fontSize: 13, fontWeight: 700 }}>
              대표 이미지 미등록
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'grid', gap: 1.5, p: 2 }}>
          <Box sx={{ minHeight: 44 }}>
            <Box sx={{ color: '#111827', fontSize: 15, fontWeight: 800, lineHeight: 1.45 }}>
              {event.title}
            </Box>
            <Box
              sx={{
                color: hasImage ? '#16a34a' : '#94a3b8',
                fontSize: 12,
                fontWeight: 700,
                mt: 0.5,
              }}
            >
              {getImageStatus(event)}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button onClick={() => openGoodsManagement(event)} size="small" variant="contained">
              굿즈 관리
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth onClick={() => openEditDialog(event)} size="small" variant="outlined">
                대표 이미지 {hasImage ? '수정' : '설정'}
              </Button>
              <Button
                color="error"
                disabled={!hasImage}
                onClick={() => setDeleteTarget(event)}
                size="small"
                variant="outlined"
              >
                삭제
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  }

  function renderMessages() {
    return (
      <>
        {errorMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
      </>
    );
  }

  function renderRepresentativeDialog() {
    return (
      <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(closeEditDialog)} open={Boolean(activeEvent)}>
        <FormDialogTitle onClose={closeEditDialog} sx={{ fontSize: 20, fontWeight: 800 }}>
          굿즈 대표 이미지 {activeEvent?.imageUrl ? '수정' : '설정'}
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            {dialogErrorMessage && <Alert severity="warning">{dialogErrorMessage}</Alert>}
            <Alert severity="info">
              정사각형(1:1)으로 표시되며, 업로드 시 영역을 조정할 수 있습니다.
            </Alert>
            <TextField InputProps={{ readOnly: true }} label="대회명" value={activeEvent?.title ?? ''} />
            <Button component="label" variant="outlined">
              이미지 선택
              <input accept="image/*" hidden onChange={handleRepresentativeImageChange} type="file" />
            </Button>
            {selectedImage && (
              <Box sx={{ color: '#475569', fontSize: 13 }}>선택한 파일: {selectedImage.name}</Box>
            )}
            {sourcePreviewUrl && renderCropEditor()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={savingRepresentative} onClick={closeEditDialog} variant="outlined">
            취소
          </Button>
          <Button disabled={savingRepresentative} onClick={handleRepresentativeSave} variant="contained">
            {savingRepresentative ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderCropEditor() {
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Box
          sx={{
            aspectRatio: '1 / 1',
            backgroundColor: '#0f172a',
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
          }}
        >
          <Cropper
            aspect={1}
            crop={crop}
            cropSize={{ width: cropBoxSize, height: cropBoxSize }}
            cropShape="rect"
            image={sourcePreviewUrl}
            objectFit="contain"
            onCropChange={setCrop}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
            showGrid
            zoom={1}
            zoomWithScroll={false}
          />
          <Box
            sx={{
              border: '2px solid #ffffff',
              boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.22)',
              height: cropBoxSize,
              left: '50%',
              pointerEvents: 'none',
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: cropBoxSize,
              zIndex: 2,
            }}
          >
            <Box
              aria-label="크롭 영역 크기 조절"
              component="button"
              onPointerDown={handleCropBoxResizeStart}
              sx={{
                backgroundColor: '#ffffff',
                border: '2px solid #2563eb',
                borderRadius: '50%',
                bottom: -9,
                cursor: 'nwse-resize',
                height: 18,
                padding: 0,
                pointerEvents: 'auto',
                position: 'absolute',
                right: -9,
                width: 18,
              }}
              type="button"
            />
          </Box>
        </Box>
        <Box sx={{ color: '#64748b', fontSize: 12 }}>
          이미지를 드래그해서 위치를 조정하고, 정사각형 영역의 모서리를 드래그해서 크기를 조정할 수 있습니다.
        </Box>
      </Box>
    );
  }

  function renderRepresentativeDeleteDialog() {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => !deletingRepresentative && setDeleteTarget(null)}
        open={Boolean(deleteTarget)}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>굿즈 대표 이미지 삭제</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
            {deleteTarget?.title} 대회의 굿즈 대표 이미지를 삭제할까요?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={deletingRepresentative} onClick={() => setDeleteTarget(null)} variant="outlined">
            취소
          </Button>
          <Button
            color="error"
            disabled={deletingRepresentative}
            onClick={handleRepresentativeDelete}
            variant="contained"
          >
            {deletingRepresentative ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderGoodsDialog() {
    return (
      <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(closeGoodsDialog)} open={goodsDialogOpen}>
        <FormDialogTitle onClose={closeGoodsDialog} sx={{ fontSize: 20, fontWeight: 800 }}>
          굿즈 추가
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            {goodsDialogErrorMessage && <Alert severity="warning">{goodsDialogErrorMessage}</Alert>}
            <Alert severity="info">
              정사각형(1:1)으로 표시되며, 업로드 시 영역을 조정할 수 있습니다.
            </Alert>
            <TextField
              label="굿즈 이름"
              onChange={(event) => setGoodsName(event.target.value)}
              value={goodsName}
            />
            <TextField
              label="설명"
              multiline
              minRows={3}
              onChange={(event) => setGoodsDescription(event.target.value)}
              value={goodsDescription}
            />
            <Button component="label" variant="outlined">
              이미지 선택
              <input accept="image/*" hidden onChange={handleGoodsImageChange} type="file" />
            </Button>
            {goodsImage && (
              <Box sx={{ color: '#475569', fontSize: 13 }}>선택한 파일: {goodsImage.name}</Box>
            )}
            {goodsPreviewUrl && renderGoodsCropEditor()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={goodsSaving} onClick={closeGoodsDialog} variant="outlined">
            취소
          </Button>
          <Button disabled={goodsSaving} onClick={handleGoodsCreate} variant="contained">
            {goodsSaving ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderGoodsCropEditor() {
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Box
          sx={{
            aspectRatio: '1 / 1',
            backgroundColor: '#0f172a',
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
          }}
        >
          <Cropper
            aspect={1}
            crop={goodsCrop}
            cropSize={{ width: goodsCropBoxSize, height: goodsCropBoxSize }}
            cropShape="rect"
            image={goodsPreviewUrl}
            objectFit="contain"
            onCropChange={setGoodsCrop}
            onCropComplete={(_, areaPixels) => setGoodsCroppedAreaPixels(areaPixels)}
            showGrid
            zoom={1}
            zoomWithScroll={false}
          />
          <Box
            sx={{
              border: '2px solid #ffffff',
              boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.22)',
              height: goodsCropBoxSize,
              left: '50%',
              pointerEvents: 'none',
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: goodsCropBoxSize,
              zIndex: 2,
            }}
          >
            <Box
              aria-label="굿즈 크롭 영역 크기 조절"
              component="button"
              onPointerDown={handleGoodsCropBoxResizeStart}
              sx={{
                backgroundColor: '#ffffff',
                border: '2px solid #2563eb',
                borderRadius: '50%',
                bottom: -9,
                cursor: 'nwse-resize',
                height: 18,
                padding: 0,
                pointerEvents: 'auto',
                position: 'absolute',
                right: -9,
                width: 18,
              }}
              type="button"
            />
          </Box>
        </Box>
        <Box sx={{ color: '#64748b', fontSize: 12 }}>
          이미지를 드래그해서 위치를 조정하고, 정사각형 영역의 모서리를 드래그해서 크기를 조정할 수 있습니다.
        </Box>
      </Box>
    );
  }

  function renderGoodsDeleteDialog() {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => !goodsDeleting && setGoodsDeleteTarget(null)}
        open={Boolean(goodsDeleteTarget)}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>굿즈 삭제</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
            {goodsDeleteTarget?.name} 굿즈를 삭제할까요?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={goodsDeleting} onClick={() => setGoodsDeleteTarget(null)} variant="outlined">
            취소
          </Button>
          <Button color="error" disabled={goodsDeleting} onClick={handleGoodsDelete} variant="contained">
            {goodsDeleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default GoodsRepresentativeImagePage;
